import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Users, Eye, UserPlus, Radio, TrendingUp, Globe, Smartphone, Monitor, Tablet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#A1A1AA] text-xs uppercase tracking-wide">
      <Icon className="w-4 h-4 text-[#00F0FF]" /> {label}
    </div>
    <div className="mt-2 text-3xl font-bold text-white">{value}</div>
    {sub && <div className="text-xs text-[#A1A1AA] mt-1">{sub}</div>}
  </div>
);

const deviceIcon = { mobile: Smartphone, desktop: Monitor, tablet: Tablet };

const TrafficPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`${API_URL}/api/analytics/overview?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (r.status === 403) throw new Error('Owner access required');
        return r.json();
      })
      .then((d) => { if (active) { setData(d); setError(null); } })
      .catch((e) => { if (active) setError(e.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [days, token]);

  const t = data?.totals || {};
  const maxViews = Math.max(1, ...(data?.timeseries || []).map((d) => d.views));

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-24">
      <div className="sticky top-0 z-10 bg-[#050505]/90 backdrop-blur border-b border-white/10 px-4 py-4 flex flex-wrap items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="traffic-back-btn" className="p-2 rounded-full hover:bg-white/10">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold">Traffic & Visitors</h1>
          <p className="text-xs text-[#A1A1AA]">Everyone visiting NLT Radio — owner only</p>
        </div>
        {t.live_now > 0 && (
          <span className="ml-auto flex items-center gap-1.5 text-xs bg-[#FF007F]/20 text-[#FF007F] px-3 py-1.5 rounded-full" data-testid="traffic-live-now">
            <span className="w-2 h-2 rounded-full bg-[#FF007F] animate-pulse" /> {t.live_now} live now
          </span>
        )}
      </div>

      <div className="px-4 pt-4 flex gap-2">
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            data-testid={`traffic-range-${d}`}
            className={`px-3 py-1.5 rounded-full text-sm ${days === d ? 'bg-[#00F0FF] text-black font-semibold' : 'bg-white/5 text-[#A1A1AA]'}`}
          >
            {d}d
          </button>
        ))}
      </div>

      {loading && <div className="p-8 text-center text-[#A1A1AA]">Loading traffic…</div>}
      {error && <div className="p-8 text-center text-[#FF007F]" data-testid="traffic-error">{error}</div>}

      {data && !loading && (
        <div className="px-4 pt-4 space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Eye} label="Page Views" value={t.pageviews?.toLocaleString()} sub={`${t.pageviews_today} today`} testid="stat-pageviews" />
            <StatCard icon={Users} label="Unique Visitors" value={t.unique_visitors?.toLocaleString()} sub={`${t.unique_today} today`} testid="stat-visitors" />
            <StatCard icon={UserPlus} label="New Signups" value={t.new_signups?.toLocaleString()} sub={`${t.signups_today} today · ${t.total_users} total`} testid="stat-signups" />
            <StatCard icon={Radio} label="Logged-in Views" value={t.authenticated_views?.toLocaleString()} sub={`${t.anonymous_views} anonymous`} testid="stat-authed" />
          </div>

          {/* Timeseries */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-4 text-sm font-semibold"><TrendingUp className="w-4 h-4 text-[#00F0FF]" /> Daily traffic</div>
            <div className="flex items-end gap-1 h-40" data-testid="traffic-chart">
              {(data.timeseries || []).length === 0 && (
                <div className="w-full text-center text-sm text-[#A1A1AA] self-center">Not much traffic in this range yet — it fills up as people visit.</div>
              )}
              {(data.timeseries || []).slice(-30).map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative">
                  <div
                    className="w-full bg-gradient-to-t from-[#00F0FF]/40 to-[#00F0FF] rounded-t"
                    style={{ height: `${(d.views / maxViews) * 100}%`, minHeight: '2px' }}
                    title={`${d.date}: ${d.views} views, ${d.visitors} visitors`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><Globe className="w-4 h-4 text-[#00F0FF]" /> Top pages</div>
              {(data.top_pages || []).map((p) => (
                <div key={p.label} className="flex justify-between text-sm py-1.5 border-b border-white/5">
                  <span className="text-[#A1A1AA] truncate">{p.label}</span>
                  <span className="font-medium">{p.count}</span>
                </div>
              ))}
              {(!data.top_pages || data.top_pages.length === 0) && <p className="text-xs text-[#A1A1AA]">No data yet</p>}
            </div>
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><TrendingUp className="w-4 h-4 text-[#00F0FF]" /> Top referrers</div>
              {(data.top_referrers || []).map((p) => (
                <div key={p.label} className="flex justify-between text-sm py-1.5 border-b border-white/5">
                  <span className="text-[#A1A1AA] truncate">{p.label}</span>
                  <span className="font-medium">{p.count}</span>
                </div>
              ))}
              {(!data.top_referrers || data.top_referrers.length === 0) && <p className="text-xs text-[#A1A1AA]">Direct / none yet</p>}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <div className="text-sm font-semibold mb-3">Devices</div>
            <div className="flex gap-4">
              {Object.entries(data.devices || {}).map(([dev, count]) => {
                const Ic = deviceIcon[dev] || Globe;
                return (
                  <div key={dev} className="flex items-center gap-2 text-sm">
                    <Ic className="w-4 h-4 text-[#00F0FF]" />
                    <span className="capitalize">{dev}</span>
                    <span className="text-[#A1A1AA]">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficPage;
